import type { QueryClient } from '@tanstack/react-query'
import { redirect } from '@tanstack/react-router'

import { customerProjectsQuery } from '@/api/configurator'
import type { QueryKeySegment } from '@/api/query-keys'
import {
  CUSTOMER_ROUTE_PREFIX,
  PROJECT_ROUTE,
  projectRoute,
} from '@/constants/routes'
import { rememberedProject } from '@/utils/remembered-project'
import { asRoute, asSearch } from '@/utils/runtime-route'

// Helpers for the advisor "shadow-view" mode. When `shadowUserId` is a valid
// number the request/URL targets a specific customer via internal endpoints;
// otherwise the caller stays on the customer's own auth path.
//
// `Number.isFinite` is critical: `Number(undefined)` = `NaN`, and
// `NaN != null` would otherwise route to `/user/NaN/…` during route hydration.
export function isShadowUser(
  shadowUserId: number | undefined,
): shadowUserId is number {
  return shadowUserId != null && Number.isFinite(shadowUserId)
}

/** The customer's projects, read once per navigation and shared with every
 *  screen that lists them. */
const projectsOf = (queryClient: QueryClient, shadowUserId?: number) =>
  queryClient.ensureQueryData(customerProjectsQuery(shadowUserId))

/** The shadowed customer a route is addressed by, or `undefined` on the
 *  customer's own routes. */
const viewerOf = (params: { userId?: string }) =>
  params.userId === undefined ? undefined : Number(params.userId)

/**
 * The project a route names, confirmed to be one this customer owns.
 *
 * Shared by the customer's own project routes and the advisor's shadow mirror:
 * an id naming nothing they own sends the reader back to where a project is
 * chosen, rather than onto a screen whose every request answers 404.
 */
export async function requireCustomerProject({
  context,
  params,
}: {
  context: { queryClient: QueryClient }
  params: { userId?: string; projectId: string }
}) {
  const shadowUserId = viewerOf(params)
  const projects = await projectsOf(context.queryClient, shadowUserId)
  const projectId = Number(params.projectId)

  if (projects.some(project => project.id === projectId)) return

  throw redirect({
    to: asRoute(toCustomerAreaRoute(PROJECT_ROUTE, shadowUserId)),
  })
}

/**
 * Sends a reader who named no project to one of theirs — the one they had open
 * last, else the first.
 *
 * A customer with nothing to open stays here, and the route renders the
 * invitation to start a project.
 */
export async function openOneProject({
  context,
  params,
}: {
  context: { queryClient: QueryClient }
  params: { userId?: string }
}) {
  const shadowUserId = viewerOf(params)
  const projects = await projectsOf(context.queryClient, shadowUserId)
  if (projects.length === 0) return

  const remembered = rememberedProject(shadowUserId)
  const known = projects.find(project => project.id === remembered)

  throw redirect({
    to: asRoute(
      toCustomerAreaRoute(
        projectRoute((known ?? projects[0]).id),
        shadowUserId,
      ),
    ),
    // Nothing remembered and more than one to choose from: the reader is shown
    // one of them and asked which they meant. Said in the address so the
    // question survives the redirect and is asked exactly once.
    search:
      known || projects.length === 1 ? undefined : asSearch({ choose: 'true' }),
  })
}

const SHADOW_API_PREFIX = '/internal/customer-shadow/user/'
const SHADOW_ROUTE_PREFIX = '/internal/customer-view/'

// Server API path flipper: customer endpoint vs `/internal/customer-shadow/…`.
export function shadowOrCustomerBase(
  customerPath: string,
  shadowSegment: string,
  shadowUserId: number | undefined,
): string {
  return isShadowUser(shadowUserId)
    ? `${SHADOW_API_PREFIX}${shadowUserId}/${shadowSegment}`
    : customerPath
}

/**
 * The customer areas the advisor can be shown, named by their first path
 * segment — the shadow tree mirrors the customer tree from there on, so the
 * rewrite is the prefix and nothing else. That is also what keeps the header's
 * active entry lit, since it marks a nav item by asking whether the path is
 * under it.
 *
 * Listed rather than derived: a customer screen without a shadow counterpart
 * has to be noticed rather than guessed at.
 */
const SHADOW_AREAS = new Set<string>(['dashboard', 'matches', 'project'])

/**
 * Rewrites a customer route for the advisor shadow view.
 *
 * A customer route in no listed area is one someone added without a shadow
 * counterpart; returning it unchanged would drop the advisor into the real
 * customer area, which is the failure this whole mechanism exists to prevent.
 * It is loud in development and inert in production, where leaving the advisor
 * on a working page beats crashing the screen.
 */
export function toCustomerAreaRoute(
  customerRoute: string,
  shadowUserId: number | undefined,
): string {
  if (!isShadowUser(shadowUserId)) return customerRoute

  const tail = customerRoute.startsWith(CUSTOMER_ROUTE_PREFIX)
    ? customerRoute.slice(CUSTOMER_ROUTE_PREFIX.length)
    : null
  const area = tail?.split('/')[0]

  if (!area || !SHADOW_AREAS.has(area)) {
    if (import.meta.env.DEV && tail) {
      console.error(
        `No shadow screen for "${customerRoute}" — add its area to SHADOW_AREAS and give it a route under ${SHADOW_ROUTE_PREFIX}$userId/, or the advisor leaves the shadow view.`,
      )
    }
    return customerRoute
  }
  return `${SHADOW_ROUTE_PREFIX}${shadowUserId}/${tail}`
}

export function shadowScopedKey<T extends readonly QueryKeySegment[]>(
  baseKey: T,
  shadowUserId: number | undefined,
): readonly QueryKeySegment[] {
  return isShadowUser(shadowUserId)
    ? [...baseKey, 'shadow', shadowUserId]
    : baseKey
}

/** Entry into the shadow view, carrying where to return to. */
export function shadowEntryHref(customerUserId: number, returnTo: string) {
  return {
    to: '/internal/customer-view/$userId/dashboard',
    params: { userId: String(customerUserId) },
    search: { from: returnTo },
  } as const
}

/**
 * Which customer's project the surrounding screens read and write.
 *
 * The mirror of the backend's `ProjectViewer`: a request names the project it
 * means, and the customer it belongs to — an advisor's shadow view names that
 * customer in the path, the customer's own session is named by its token. One
 * object rather than two parameters threaded side by side, because a surface
 * that passed one and forgot the other read the right project of the wrong
 * customer, or the wrong project of the right one.
 */
export type CustomerScope = {
  /** Set while an advisor is looking at this customer's area. */
  shadowUserId?: number
  projectId: number
}

/** Where a customer's projects are listed — the one read that names none. */
export const customerProjectsBase = (shadowUserId?: number): string =>
  shadowOrCustomerBase('/customer/projects', 'projects', shadowUserId)

/** The API path every project-scoped customer request hangs off. */
export const customerProjectBase = ({
  shadowUserId,
  projectId,
}: CustomerScope): string =>
  `${customerProjectsBase(shadowUserId)}/${projectId}`

/**
 * The same, for the two surfaces that span screens with a project and screens
 * without — the advisor's shell and the dashboard. A query keyed by one of
 * these answers nothing until the project is known.
 */
export type MaybeProjectScope = Partial<CustomerScope>

/** The query key under which data belonging to one project of one customer is
 *  held — the shadow scoping plus the project. */
export function projectScopedKey<T extends readonly QueryKeySegment[]>(
  baseKey: T,
  { shadowUserId, projectId }: MaybeProjectScope,
): readonly QueryKeySegment[] {
  return [
    ...shadowScopedKey(baseKey, shadowUserId),
    'project',
    projectId ?? null,
  ]
}
